import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AsyncReduxState, initialAsyncReduxState } from '../../../redux/@config/AsyncReduxState'
import { FinishedTracksSorting, Track } from '../../entities/Track/Track'

export const initialFinishedTracksSorting: FinishedTracksSorting = {
    active: 'checkIn',
    direction: 'desc',
}

export type TracksState = AsyncReduxState<{
    loadingTrackIds: string[];
    tracksToReview: Track[];
    finishedTracks: Track[];
    finishedTracksSorting: FinishedTracksSorting;
    isExportLoading: boolean;
    isExportSuccessful: boolean;
}>;

const initialState: TracksState = {
    ...initialAsyncReduxState,
    loadingTrackIds: [],
    tracksToReview: [],
    finishedTracks: [],
    finishedTracksSorting: initialFinishedTracksSorting,
    isExportLoading: false,
    isExportSuccessful: false,
}

export const TracksSlice = createSlice({
    name: 'tracksReducer',
    initialState,
    reducers: {
        setIsLoading(state, action: PayloadAction<boolean>): TracksState {
            return {
                ...state,
                isLoading: action.payload,
            }
        },
        setIsExportLoading(state, action: PayloadAction<boolean>): TracksState {
            return {
                ...state,
                isExportLoading: action.payload,
            }
        },
        setIsExportSuccessful(state, action: PayloadAction<boolean>): TracksState {
            return {
                ...state,
                isExportSuccessful: action.payload,
            }
        },
        setError(state, action: PayloadAction<string>): TracksState {
            return {
                ...state,
                error: action.payload,
            }
        },
        setTracksToReview(state, action: PayloadAction<Track[]>): TracksState {
            return {
                ...state,
                tracksToReview: action.payload,
            }
        },
        setFinishedTracks(state, action: PayloadAction<Track[]>): TracksState {
            return {
                ...state,
                finishedTracks: action.payload,
            }
        },
        setFinishedTracksSorting(state, action: PayloadAction<FinishedTracksSorting>): TracksState {
            return {
                ...state,
                finishedTracksSorting: action.payload,
            }
        },
        trackAdded(state, action: PayloadAction<Track>): TracksState {
            if (action.payload.accepted === false && action.payload.checkInOutPeriod.end) {
                if (state.tracksToReview.some((track) => track.id === action.payload.id)) {
                    throw Error(`track with id: ${action.payload.id} already exists`)
                }

                return {
                    ...state,
                    tracksToReview: [...state.tracksToReview, action.payload],
                }
            }

            if (action.payload.accepted === true && action.payload.checkInOutPeriod.end) {
                if (state.finishedTracks.some((track) => track.id === action.payload.id)) {
                    throw Error(`track with id: ${action.payload.id} already exists`)
                }

                return {
                    ...state,
                    finishedTracks: [...state.finishedTracks, action.payload],
                }
            }

            return state
        },
        trackPatched(state, action: PayloadAction<Partial<Track> & { id: string }>): TracksState {
            const trackInReview = state.tracksToReview.find(track => track.id === action.payload.id)
            const trackInFinished = state.finishedTracks.find(track => track.id === action.payload.id)
            const trackToUpdate = trackInReview || trackInFinished

            if (! trackToUpdate) {
                console.error(`track with id: ${action.payload.id} does not exist yet`)

                return state
            }

            const updatedTrack = { ...trackToUpdate, ...action.payload }

            if (updatedTrack.accepted === false && updatedTrack.checkInOutPeriod.end) {
                return {
                    ...state,
                    tracksToReview: trackInReview
                        ? state.tracksToReview.map(track => (track.id === action.payload.id ? updatedTrack : track))
                        : [...state.tracksToReview, updatedTrack],
                    finishedTracks: state.finishedTracks.filter(track => track.id !== action.payload.id),
                }
            }

            if (updatedTrack.accepted === true && updatedTrack.checkInOutPeriod.end) {
                return {
                    ...state,
                    tracksToReview: state.tracksToReview.filter(track => track.id !== action.payload.id),
                    finishedTracks: trackInFinished
                        ? state.finishedTracks.map(track => (track.id === action.payload.id ? updatedTrack : track))
                        : [...state.finishedTracks, updatedTrack],
                }
            }

            return state
        },
        trackDeleted(state, action: PayloadAction<{ id: string }>): TracksState {
            return {
                ...state,
                tracksToReview: state.tracksToReview.filter(track => track.id !== action.payload.id),
                finishedTracks: state.finishedTracks.filter(track => track.id !== action.payload.id),
            }
        },
        setLoadingTrack(state, action: PayloadAction<string>): TracksState {
            return {
                ...state,
                loadingTrackIds: [...state.loadingTrackIds, action.payload],
            }
        },
        removeLoadingTrack(state, action: PayloadAction<string>): TracksState {
            return {
                ...state,
                loadingTrackIds: state.loadingTrackIds.filter(trackId => trackId !== action.payload),
            }
        },
    },
})

export const {
    setIsLoading,
    setIsExportLoading,
    setIsExportSuccessful,
    setError,
    setTracksToReview,
    setFinishedTracks,
    setFinishedTracksSorting,
    trackAdded,
    trackPatched,
    trackDeleted,
    setLoadingTrack,
    removeLoadingTrack,
} = TracksSlice.actions

export default TracksSlice.reducer
